import { Check } from "lucide-react";
import React from "react";
import { FALLBACK_STEP, type AuditTimelineItem } from "../utils/auditTimeline";

export interface AuditTimelineProps {
	/**
	 * Timeline items, already built by `buildAuditTimeline`. The panel owns
	 * the derivation because it also needs the item count for its header
	 * badge; passing the result down keeps that a single computation.
	 */
	items: AuditTimelineItem[];
}

/**
 * One semantic step: a tick, a line, and a count when the row collapses
 * several identical steps.
 *
 * The tick, not a per-kind icon (DOP-492). The mapper's emoji stays the
 * semantic key inside `buildAuditTimeline`, but on the page a column of
 * different pictograms read as categories the reader was meant to tell
 * apart; every row here says the same thing — this happened — and a
 * uniform tick says it without inventing a taxonomy.
 *
 * There is deliberately nothing to expand. The audit log is a customer
 * trail, and the raw material behind a step — bash command lines, MCP
 * arguments, tool output — is engineering detail that reads as noise (and
 * exposes sandbox internals) in that context. The raw blocks are still
 * persisted on the message and still flow to Langfuse, which is where
 * anyone debugging a run should be looking.
 */
const AuditStepRow: React.FC<{ label: string; count?: number }> = ({
	label,
	count = 1,
}) => (
	<div className="trace-step audit-step">
		<span className="audit-step-tick" aria-hidden="true">
			<Check className="audit-step-tick-icon" />
		</span>
		<div className="trace-step-content">
			<div className="audit-step-header">
				<span className="audit-step-label">{label}</span>
				{count > 1 && <span className="audit-step-count">×{count}</span>}
			</div>
		</div>
	</div>
);

/**
 * The audit-trail timeline: semantic one-line steps with the model's
 * interim narration interleaved at its true position between them. The
 * narration is styled as the "model is working" voice it is — it never
 * competes with the answer, which lives in the chat bubble.
 */
const AuditTimeline: React.FC<AuditTimelineProps> = ({ items }) => {
	return (
		<>
			{items.map((item: AuditTimelineItem) => {
				if (item.kind === "narration") {
					return (
						<div key={item.key} className="trace-step audit-narration">
							<span className="trace-step-bullet" aria-hidden="true" />
							<div className="trace-step-content">
								<p className="audit-narration-text">{item.text}</p>
							</div>
						</div>
					);
				}
				if (item.kind === "raw") {
					// A trace block the mapper has no rule for. It still gets a
					// line — the trail should not silently skip work that
					// happened — but the generic one, never its payload.
					return (
						<AuditStepRow key={item.key} label={FALLBACK_STEP.label} />
					);
				}
				return (
					<AuditStepRow
						key={item.key}
						label={item.label}
						count={item.count}
					/>
				);
			})}
		</>
	);
};

export default AuditTimeline;
